/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import store from '@/store';
import RootStateType from '@/store/types';
import generateMutations, {
  UPDATE_STATE_TYPE,
} from '@/store/generate-mutations';

import { ActionContext, Module } from 'vuex';
import type { OptionItem } from '@/model/OptionItem';
import type { SelectOptionDataType } from '@hb/finance-ui/src/types/shared';
import UserPersonalInfoApi from '@/api/userPersonalInfo';
import { UserVo } from '@/vo/user';
import type { ProfileFormData, StateType } from './types';

// 将{name, code}转换成{value, label}格式
const transformOptions = (options: OptionItem[]): SelectOptionDataType[] => options.map((item) => ({
  value: item.code,
  label: item.name,
}));

// 将{value: label}装换成{value, label}格式
const transformDictionary = (
  columns: Dictionary<string>,
): SelectOptionDataType[] => Object.keys(columns).map((value: string) => ({
  value,
  label: columns[value],
}));

// 计算指定长度的子字符串
const getPartialPrefix = (
  target: string | number, prefixLength: number,
) => String(target).substr(0, prefixLength);

// 按照省市区归属上级进行分组
const groupByValuePrefix = (
  columns: SelectOptionDataType[],
  prefixLength: number,
): Dictionary<typeof columns> => {
  const data: Dictionary<SelectOptionDataType[]> = {};

  columns.forEach((item) => {
    const groupName = getPartialPrefix(item.value, prefixLength);

    data[groupName] = data[groupName] || [];
    data[groupName].push(item);
  });

  return data;
};

const createEmptyContact = (): UserVo.ContactInfo => ({
  contactNo: undefined,
  contactName: '',
  contactPhone: '',
  contactRelation: undefined,
});

const moduleStore: Module<StateType, RootStateType> = {
  namespaced: true,
  state: {
    formData: {
      updateUserEmergencyContacts: [],
    } as ProfileFormData,
    userEmergencyContacts: [createEmptyContact(), createEmptyContact()],
    userBaseId: null,
  },
  actions: {
    // 初始化表单数据
    async initFormData(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const { formData = Object.create(null) } = context.state;
        const [personalInfo, supplyPersonalInfo] = await Promise.all([
          UserPersonalInfoApi.getZYQ(), UserPersonalInfoApi.getPersonalInfo(),
        ]);

        if (personalInfo) {
          const {
            educationDegree,
            liveAddress,
            liveAddressDetails,
            preTaxMonthSalary,
            realName,
            userEmergencyContacts,
          } = personalInfo;
          const {
            educationDegree: educationDegreeReal,
            liveAddress: liveAddressReal,
            liveAddressDetails: liveAddressDetailsReal,
            preTaxMonthSalary: preTaxMonthSalaryReal,
            realName: realNameReal,
          } = formData || {};
          const cbLiveAddress = liveAddress || liveAddressReal;
          /** “居住地”判空，避免liveAddress是空对象“{}”，导致下拉框初始化异常 */
          const formatLiveAddress = cbLiveAddress && (
            cbLiveAddress.provinceCode || cbLiveAddress.cityCode || cbLiveAddress.districtCode)
            ? cbLiveAddress
            : null;

          /** 服务数据-”工作性质“返回 -1 时，实际是无效的空值，在此清空数值，避免后续bug问题 */
          if (supplyPersonalInfo.role === -1) {
            supplyPersonalInfo.role = undefined;
          }

          context.dispatch(UPDATE_STATE_TYPE, {
            formData: {
              ...formData,
              /** 渠道定制化的补充个人信息字段(即对应supplyNeedConfig接口的字段配置-数据) */
              ...(supplyPersonalInfo || {}),
              educationDegree: educationDegree === undefined
                ? educationDegreeReal : educationDegree,
              liveAddress: formatLiveAddress,
              liveAddressDetails: liveAddressDetails || liveAddressDetailsReal,
              preTaxMonthSalary: preTaxMonthSalary || preTaxMonthSalaryReal,
              realName: realName || realNameReal,
              updateUserEmergencyContacts: [],
            },
            userEmergencyContacts:
              userEmergencyContacts && userEmergencyContacts.length
                ? userEmergencyContacts : [
                  createEmptyContact(),
                  createEmptyContact(),
                ],
            userBaseId: supplyPersonalInfo.id,
          });
        }

        return Promise.resolve();
      } catch (error) {
        return Promise.resolve();
      }
    },
  },
};

store.registerModule(
  'personalProfile',
  generateMutations<StateType>(moduleStore),
  {
    preserveState: false,
  },
);
